import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RefreshCw, Search, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';

interface SearchLog {
  id: number;
  search_term: string;
  search_type: string;
  results_count: number;
  selected_length: number | null;
  created_at: string;
}

const SEARCH_TYPE_LABELS: Record<string, string> = {
  word: 'Ord',
  question: 'Ledtråd',
  combined: 'Avancerad',
  anagram: 'Anagram',
};

export const SearchHistoryAdmin = () => {
  const [logs, setLogs] = useState<SearchLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    avgResults: 0,
  });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('search_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (typeFilter !== 'all') {
        query = query.eq('search_type', typeFilter);
      }

      if (searchFilter.trim()) {
        query = query.ilike('search_term', `%${searchFilter.trim()}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      setLogs(data || []);

      // Fetch stats
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const { count: totalCount } = await supabase
        .from('search_logs')
        .select('*', { count: 'exact', head: true });

      const { count: todayCount } = await supabase
        .from('search_logs')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', todayStart.toISOString());

      const { count: weekCount } = await supabase
        .from('search_logs')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', weekStart.toISOString());

      const { data: avgData } = await supabase
        .from('search_logs')
        .select('results_count')
        .limit(1000);

      const avgResults = avgData && avgData.length > 0
        ? Math.round(avgData.reduce((sum, r) => sum + (r.results_count || 0), 0) / avgData.length)
        : 0;

      setStats({
        total: totalCount || 0,
        today: todayCount || 0,
        thisWeek: weekCount || 0,
        avgResults,
      });

    } catch (error) {
      console.error('Error fetching search logs:', error);
      toast.error('Kunde inte hämta sökhistorik');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [typeFilter, searchFilter]);

  const handleClearOldLogs = async () => {
    if (!confirm('Är du säker på att du vill radera sökloggar äldre än 30 dagar?')) return;
    
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const { error } = await supabase
        .from('search_logs')
        .delete()
        .lt('created_at', thirtyDaysAgo.toISOString());

      if (error) throw error;
      toast.success('Gamla sökloggar raderade');
      fetchLogs();
    } catch (error) {
      console.error('Error clearing logs:', error);
      toast.error('Kunde inte radera loggar');
    }
  };

  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), 'dd MMM HH:mm', { locale: sv });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-display">Sökhistorik</CardTitle>
            <CardDescription>
              Översikt över sökningar på hemsidan
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleClearOldLogs}>
              <Trash2 className="h-4 w-4 mr-2" />
              Rensa &gt;30 dagar
            </Button>
            <Button variant="outline" size="sm" onClick={fetchLogs}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-muted rounded-lg text-center">
            <p className="text-2xl font-bold text-foreground">{stats.total.toLocaleString('sv-SE')}</p>
            <p className="text-sm text-muted-foreground">Totalt</p>
          </div>
          <div className="p-4 bg-muted rounded-lg text-center">
            <p className="text-2xl font-bold text-foreground">{stats.today.toLocaleString('sv-SE')}</p>
            <p className="text-sm text-muted-foreground">Idag</p>
          </div>
          <div className="p-4 bg-muted rounded-lg text-center">
            <p className="text-2xl font-bold text-foreground">{stats.thisWeek.toLocaleString('sv-SE')}</p>
            <p className="text-sm text-muted-foreground">Senaste 7 dagarna</p>
          </div>
          <div className="p-4 bg-muted rounded-lg text-center">
            <p className="text-2xl font-bold text-foreground">{stats.avgResults}</p>
            <p className="text-sm text-muted-foreground">Snitt träffar</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Filtrera på sökterm..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Alla typer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla typer</SelectItem>
              <SelectItem value="word">Ord</SelectItem>
              <SelectItem value="question">Ledtråd</SelectItem>
              <SelectItem value="combined">Avancerad</SelectItem>
              <SelectItem value="anagram">Anagram</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Ingen sökhistorik hittades
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tidpunkt</TableHead>
                  <TableHead>Sökterm</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead className="text-right">Träffar</TableHead>
                  <TableHead className="text-right">Längd</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </TableCell>
                    <TableCell className="font-medium max-w-xs truncate">
                      {log.search_term}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-secondary text-secondary-foreground">
                        {SEARCH_TYPE_LABELS[log.search_type] || log.search_type}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {log.results_count}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {log.selected_length || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {logs.length > 0 && (
          <p className="text-sm text-muted-foreground text-center">
            Visar {logs.length} av {stats.total.toLocaleString('sv-SE')} sökningar
          </p>
        )}
      </CardContent>
    </Card>
  );
};
